import React from 'react';
import { ArrowLeft, ExternalLink, Bot, MessageSquare, Edit, Target, Zap, Search, Cpu, CheckCircle, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Link } from 'react-router-dom';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';

const AITypePage = ({ aiType, onBack }) => {
  const aiData = {
    'conversational': {
      title: 'IA Conversationnelles',
      description: 'Les assistants IA pour dialogue et rédaction',
      icon: MessageSquare,
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'from-blue-50 via-white to-indigo-50',
      ais: [
        {
          name: 'ChatGPT',
          company: 'OpenAI',
          description: 'L\'IA conversationnelle la plus populaire, excellente pour la rédaction, l\'analyse et la résolution de problèmes complexes.',
          features: ['Rédaction avancée', 'Analyse de documents', 'Code et programmation', 'Recherche et synthèse'],
          pricing: 'Gratuit / Plus 18€/mois',
          website: 'https://chat.openai.com',
          rating: 5
        },
        {
          name: 'Claude (Anthropic)',
          company: 'Anthropic',
          description: 'IA axée sur la sécurité et l\'analyse approfondie, excellente pour les tâches complexes et l\'analyse de documents longs.',
          features: ['Analyse de documents longs', 'Raisonnement complexe', 'Sécurité renforcée', 'Constitution helper'],
          pricing: 'Gratuit / Pro 18€/mois',
          website: 'https://claude.ai',
          rating: 5
        },
        {
          name: 'Gemini',
          company: 'Google',
          description: 'L\'IA de Google intégrée à l\'écosystème Google Workspace, excellente pour la recherche en temps réel.',
          features: ['Intégration Google', 'Recherche temps réel', 'Multimodal', 'Google Workspace'],
          pricing: 'Gratuit / Advanced 18€/mois',
          website: 'https://gemini.google.com',
          rating: 4
        },
        {
          name: 'Microsoft Copilot',
          company: 'Microsoft',
          description: 'Assistant IA intégré dans Windows et Office 365, parfait pour la productivité Microsoft.',
          features: ['Intégration Office 365', 'Assistant Windows', 'Bing Chat', 'Productivité'],
          pricing: 'Gratuit / Pro 18€/mois',
          website: 'https://copilot.microsoft.com',
          rating: 4
        },
        {
          name: 'Lumo',
          company: 'Proton',
          description: 'IA conversationnelle axée sur la confidentialité maximale par les créateurs de ProtonMail et ProtonVPN.',
          features: ['Confidentialité totale', 'Chiffrement bout en bout', 'Serveurs Suisse', 'Zéro tracking'],
          pricing: 'Gratuit / Pro 15€/mois',
          website: 'https://lumo.proton.me/u/0/',
          rating: 4
        }
      ]
    },
    'creative': {
      title: 'IA Créatives',
      description: 'Génération d\'images et contenu artistique',
      icon: Edit,
      color: 'from-pink-500 to-purple-600',
      bgColor: 'from-pink-50 via-white to-purple-50',
      ais: [
        {
          name: 'Midjourney',
          company: 'Midjourney Inc',
          description: 'La référence pour la génération d\'images artistiques et créatives de haute qualité.',
          features: ['Images artistiques', 'Styles variés', 'Haute qualité', 'Communauté active'],
          pricing: '9€/mois (Basic)',
          website: 'https://midjourney.com',
          rating: 5
        },
        {
          name: 'DALL-E 3',
          company: 'OpenAI',
          description: 'Générateur d\'images intégré à ChatGPT, excellent pour des visuels précis et cohérents.',
          features: ['Intégration ChatGPT', 'Génération précise', 'Texte dans images', 'Modifications faciles'],
          pricing: 'Inclus ChatGPT Plus',
          website: 'https://chat.openai.com',
          rating: 4
        },
        {
          name: 'Stable Diffusion',
          company: 'Stability AI',
          description: 'IA open-source pour génération d\'images, personnalisable et sans limites.',
          features: ['Open-source', 'Personnalisable', 'Sans limites', 'Communauté'],
          pricing: 'Gratuit / Cloud payant',
          website: 'https://stability.ai',
          rating: 4
        },
        {
          name: 'Adobe Firefly',
          company: 'Adobe',
          description: 'IA créative intégrée aux outils Adobe, parfaite pour les professionnels du design.',
          features: ['Intégration Adobe', 'Commercial safe', 'Retouche photo', 'Design professionnel'],
          pricing: 'Inclus Creative Cloud',
          website: 'https://firefly.adobe.com',
          rating: 4
        }
      ]
    },
    'specialized': {
      title: 'IA Spécialisées',
      description: 'Outils experts par secteur d\'activité',
      icon: Target,
      color: 'from-green-500 to-teal-600',
      bgColor: 'from-green-50 via-white to-teal-50',
      ais: [
        {
          name: 'Perplexity',
          company: 'Perplexity AI',
          description: 'Moteur de recherche IA avec sources citées, parfait pour la recherche académique et professionnelle.',
          features: ['Sources citées', 'Recherche temps réel', 'Résumés précis', 'Mode focus'],
          pricing: 'Gratuit / Pro 18€/mois',
          website: 'https://perplexity.ai',
          rating: 5
        },
        {
          name: 'Otter.ai',
          company: 'Otter.ai',
          description: 'Transcription en temps réel et automatique pour réunions, interviews et conférences.',
          features: ['Transcription temps réel', 'Identification locuteurs', 'Résumés automatiques', 'Intégration Zoom/Teams'],
          pricing: 'Gratuit / Pro 8€/mois',
          website: 'https://otter.ai',
          rating: 4
        },
        {
          name: 'Whisper AI',
          company: 'OpenAI',
          description: 'IA de transcription ultra-précise d\'OpenAI, fonctionne hors ligne en 99 langues.',
          features: ['99 langues supportées', 'Transcription hors ligne', 'Précision exceptionnelle', 'Open source'],
          pricing: 'Gratuit',
          website: 'https://openai.com/research/whisper',
          rating: 5
        },
        {
          name: 'Rev AI',
          company: 'Rev',
          description: 'Service de transcription professionnel avec options automatique et humaine.',
          features: ['Transcription automatique', 'Révision humaine', 'API professionnelle', 'Formats multiples'],
          pricing: '1,80€/min automatique / 20€/min humaine',
          website: 'https://rev.com',
          rating: 4
        },
        {
          name: 'Notion AI',
          company: 'Notion',
          description: 'IA intégrée à Notion pour l\'organisation et la gestion de connaissances.',
          features: ['Intégration Notion', 'Résumés automatiques', 'Génération contenu', 'Organisation'],
          pricing: '7€/mois par membre',
          website: 'https://notion.so',
          rating: 4
        },
        {
          name: 'Cursor',
          company: 'Anysphere',
          description: 'Éditeur de code avec IA intégrée, révolutionne la programmation avec assistance contexte.',
          features: ['Éditeur IA natif', 'Chat avec codebase', 'Génération code', 'Refactoring intelligent'],
          pricing: '18€/mois (Pro)',
          website: 'https://cursor.sh',
          rating: 5
        },
        {
          name: 'Gamma',
          company: 'Gamma',
          description: 'Création de présentations avec IA, génère des slides professionnelles automatiquement.',
          features: ['Présentations auto', 'Design professionnel', 'Templates', 'Collaboration'],
          pricing: 'Gratuit / Pro 9€/mois',
          website: 'https://gamma.app',
          rating: 4
        },
        {
          name: 'Trint',
          company: 'Trint',
          description: 'Plateforme de transcription collaborative avec édition intelligente et recherche avancée.',
          features: ['Transcription collaborative', 'Édition temps réel', 'Recherche dans audio', 'Export multiformat'],
          pricing: 'Pro 43€/mois',
          website: 'https://trint.com',
          rating: 4
        }
      ]
    },
    'productivity': {
      title: 'IA Productivité',
      description: 'Automatisation et optimisation du travail',
      icon: Zap,
      color: 'from-orange-500 to-red-600',
      bgColor: 'from-orange-50 via-white to-red-50',
      ais: [
        {
          name: 'Zapier AI',
          company: 'Zapier',
          description: 'Automatisation intelligente des workflows entre applications, sans code.',
          features: ['Automatisation no-code', '5000+ intégrations', 'Workflows intelligents', 'Triggers IA'],
          pricing: 'Gratuit / Pro 18€/mois',
          website: 'https://zapier.com',
          rating: 5
        },
        {
          name: 'Motion',
          company: 'Motion',
          description: 'Assistant IA pour planification automatique et gestion du temps optimal.',
          features: ['Planification auto', 'Gestion temps', 'Calendrier intelligent', 'Priorisation'],
          pricing: '31€/mois',
          website: 'https://usemotion.com',
          rating: 4
        },
        {
          name: 'Superhuman',
          company: 'Superhuman',
          description: 'Client email avec IA pour une gestion ultra-rapide et efficace de vos emails.',
          features: ['Email ultra-rapide', 'Raccourcis IA', 'Triage intelligent', 'Productivité'],
          pricing: '27€/mois',
          website: 'https://superhuman.com',
          rating: 4
        },
        {
          name: 'Reclaim AI',
          company: 'Reclaim AI',
          description: 'Planificateur IA qui optimise automatiquement votre calendrier pour maximiser la productivité.',
          features: ['Optimisation calendrier', 'Blocs de focus', 'Habitudes', 'Intégrations'],
          pricing: 'Gratuit / Pro 11€/mois',
          website: 'https://reclaim.ai',
          rating: 4
        }
      ]
    },
    'research': {
      title: 'IA Recherche',
      description: 'Moteurs de recherche et analyse intelligente',
      icon: Search,
      color: 'from-purple-500 to-indigo-600',
      bgColor: 'from-purple-50 via-white to-indigo-50',
      ais: [
        {
          name: 'Perplexity Pro',
          company: 'Perplexity AI',
          description: 'Version avancée avec accès GPT-4 et Claude pour recherches approfondies.',
          features: ['GPT-4 + Claude', 'Sources académiques', 'Recherche approfondie', 'Mode focus'],
          pricing: '20$/mois',
          website: 'https://perplexity.ai',
          rating: 5
        },
        {
          name: 'Consensus',
          company: 'Consensus',
          description: 'Moteur de recherche scientifique IA pour trouver et résumer des études académiques.',
          features: ['Recherche scientifique', 'Études académiques', 'Résumés automatiques', 'Fact-checking'],
          pricing: 'Gratuit / Pro 9$/mois',
          website: 'https://consensus.app',
          rating: 4
        },
        {
          name: 'Elicit',
          company: 'Elicit',
          description: 'Assistant de recherche IA pour la littérature scientifique et l\'analyse de données.',
          features: ['Littérature scientifique', 'Extraction données', 'Synthèse recherche', 'Citations'],
          pricing: 'Gratuit / Plus 11€/mois',
          website: 'https://elicit.org',
          rating: 4
        },
        {
          name: 'Semantic Scholar',
          company: 'Allen Institute',
          description: 'Moteur de recherche académique IA pour découvrir et analyser la littérature scientifique.',
          features: ['Publications académiques', 'Graphique citations', 'Recommandations', 'Gratuit'],
          pricing: 'Gratuit',
          website: 'https://semanticscholar.org',
          rating: 4
        }
      ]
    },
    'coding': {
      title: 'IA Programmation',
      description: 'Assistance au développement et au code',
      icon: Cpu,
      color: 'from-cyan-500 to-blue-600',
      bgColor: 'from-cyan-50 via-white to-blue-50',
      ais: [
        {
          name: 'GitHub Copilot',
          company: 'GitHub/Microsoft',
          description: 'Assistant de code IA intégré aux éditeurs, génère du code contextuel en temps réel.',
          features: ['Autocomplétion code', 'Intégration IDE', 'Langages multiples', 'Suggestions contexte'],
          pricing: '9€/mois',
          website: 'https://github.com/features/copilot',
          rating: 5
        },
        {
          name: 'Cursor',
          company: 'Anysphere',
          description: 'Éditeur de code nouvelle génération avec IA native pour une programmation révolutionnée.',
          features: ['Éditeur IA natif', 'Chat avec codebase', 'Génération code', 'Refactoring intelligent'],
          pricing: '20$/mois',
          website: 'https://cursor.sh',
          rating: 5
        },
        {
          name: 'Codeium',
          company: 'Codeium',
          description: 'Alternative gratuite à Copilot avec support étendu des langages et éditeurs.',
          features: ['Gratuit', '70+ langages', 'Intégrations multiples', 'Chat IA'],
          pricing: 'Gratuit',
          website: 'https://codeium.com',
          rating: 4
        },
        {
          name: 'Replit AI',
          company: 'Replit',
          description: 'IA de programmation intégrée à l\'environnement de développement en ligne Replit.',
          features: ['Développement en ligne', 'Génération projets', 'Débogage IA', 'Collaboration'],
          pricing: '6€/mois',
          website: 'https://replit.com',
          rating: 4
        }
      ]
    }
  };

  const currentAI = aiData[aiType];
  if (!currentAI) return null;

  const IconComponent = currentAI.icon;

  return (
    <div className={`min-h-screen bg-gradient-to-br ${currentAI.bgColor}`}>
      {/* Full Width Banner */}
      <section className={`relative overflow-hidden bg-gradient-to-r ${currentAI.color}`}>
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center">
            <div className="bg-white bg-opacity-20 p-4 rounded-2xl mr-6 backdrop-blur-sm">
              <IconComponent className="h-16 w-16 text-white" />
            </div>
            <div>
              <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-4">{currentAI.title}</h1>
              <p className="text-white text-xl md:text-2xl max-w-4xl leading-relaxed opacity-90">
                {currentAI.description}
              </p>
            </div>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-cyan-300 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-pink-300 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-blue-300 rounded-full opacity-25 animate-ping"></div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb */}
        <Breadcrumb 
          items={[
            { id: 'intelligence-artificielle', label: 'Intelligence Artificielle' },
            { id: aiType, label: currentAI.title }
          ]}
        />

        {/* Back Button */}
        <div className="mb-8">
          <Button 
            onClick={() => window.history.back()}
            variant="ghost" 
            className="hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour à la page précédente
          </Button>
        </div>

        {/* AI Tools List */}
        <div className="space-y-4">
          {currentAI.ais.map((ai, index) => (
            <Card key={index} className="group cursor-pointer transition-all duration-300 hover:shadow-xl border-0 overflow-hidden bg-white">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  {/* AI Logo/Icon Section */}
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${currentAI.color} shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                    <IconComponent className="h-8 w-8 text-white" />
                  </div>
                  
                  {/* Content Section */}
                  <div className="flex-grow">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-1 group-hover:text-indigo-600 transition-colors duration-300">
                          {ai.name}
                        </h3>
                        <p className="text-base text-gray-500 mb-3">{ai.company}</p>
                      </div>
                      
                      {/* Pricing Badge */}
                      <Badge className="bg-green-100 text-green-800 px-3 py-1 text-sm font-semibold">
                        {ai.pricing}
                      </Badge>
                    </div>
                    
                    <p className="text-gray-700 text-base leading-relaxed mb-4">
                      {ai.description}
                    </p>
                    
                    {/* Features */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                      {ai.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center gap-2">
                          <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                          <span className="text-xs text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Action Button */}
                    <div className="flex justify-end">
                      <Button 
                        onClick={() => window.open(ai.website, '_blank')}
                        className={`bg-gradient-to-r ${currentAI.color} hover:shadow-lg text-white font-bold px-4 py-2 rounded-full transition-all duration-300 transform hover:scale-105 text-sm`}
                      >
                        Visiter le site officiel
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
        
        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <Link to="/intelligence-artificielle">
            <Button className={`bg-gradient-to-r ${currentAI.color} hover:shadow-xl text-white font-bold px-8 py-4 rounded-full text-lg transition-all duration-300 transform hover:scale-105`}>
              <Bot className="h-6 w-6 mr-3" />
              Voir tous les guides IA
              <ArrowRight className="ml-3 h-6 w-6" />
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default AITypePage;